/**
 * Shopify Session Token utilities for App Bridge v4
 * Used for "Built for Shopify" certification compliance
 *
 * These utilities handle:
 * - Detecting if the app is running inside Shopify Admin iframe
 * - Getting fresh session tokens for API authentication
 * - Validating App Bridge initialization
 * - Caching tokens to avoid repeated fetches (tokens expire after 60s)
 */

/**
 * Check if the app is running inside the Shopify Admin iframe
 * This checks for both the App Bridge global object and iframe context
 */
export const isShopifyEmbedded = (): boolean => {
  if (typeof window === 'undefined') return false

  try {
    // Check if we're in an iframe
    const isInIframe = window !== window.top

    // Check if App Bridge is available (indicates Shopify context)
    const hasAppBridge = typeof window.shopify !== 'undefined'

    // Also check URL params that indicate Shopify context
    const urlParams = new URLSearchParams(window.location.search)
    const hasShopifyParams = !!(urlParams.get('shop') && urlParams.get('host'))

    return isInIframe && (hasAppBridge || hasShopifyParams)
  } catch (e) {
    // Cross-origin iframe access will throw - means we're in an iframe
    return true
  }
}

/**
 * Single promise that resolves when App Bridge is ready
 * This is shared across all requests to avoid repeated waiting
 */
let appBridgeReadyPromise: Promise<boolean> | null = null

const waitForAppBridge = (): Promise<boolean> => {
  // Return existing promise if already waiting/resolved
  if (appBridgeReadyPromise) return appBridgeReadyPromise

  appBridgeReadyPromise = new Promise((resolve) => {
    // If already available, resolve immediately
    if (window.shopify?.idToken) {
      resolve(true)
      return
    }

    // Check periodically, but only once globally (max 1s total wait)
    let attempts = 0
    const check = () => {
      if (window.shopify?.idToken) {
        resolve(true)
      } else if (attempts < 10) {
        attempts++
        setTimeout(check, 100)
      } else {
        resolve(false) // Give up after 1s
      }
    }
    check()
  })

  return appBridgeReadyPromise
}

/**
 * Token cache to avoid fetching on every request
 * Tokens expire after 60s, we cache for 50s to be safe
 */
let tokenCache: { token: string; expires: number } | null = null

/**
 * Get a session token from Shopify App Bridge
 * Uses caching to avoid repeated fetches - tokens are cached for 50s
 *
 * @returns The session token string, or null if not in Shopify context
 */
export const getShopifySessionToken = async (): Promise<string | null> => {
  if (typeof window === 'undefined') return null

  // Return cached token if still valid (with 10s buffer before expiry)
  if (tokenCache && Date.now() < tokenCache.expires) {
    return tokenCache.token
  }

  // Wait for App Bridge once (shared across all concurrent requests)
  const ready = await waitForAppBridge()
  if (!ready || !window.shopify?.idToken) return null

  try {
    // Add timeout to prevent hanging if App Bridge is not properly initialized
    const tokenPromise = window.shopify.idToken()
    const timeoutPromise = new Promise<null>((_, reject) =>
      setTimeout(() => reject(new Error('Session token fetch timeout')), 3000)
    )

    const token = await Promise.race([tokenPromise, timeoutPromise])
    
    if (token) {
      // Cache token for 50s (tokens expire after 60s)
      tokenCache = { token, expires: Date.now() + 50000 }
    }
    
    return token
  } catch (error) {
    console.warn('[Shopify] Session token fetch failed:', error)
    return null
  }
}

/**
 * Log App Bridge v4 diagnostic information (for debugging)
 * Call this manually in browser console: logAppBridgeDiagnostics()
 */
export const logAppBridgeDiagnostics = (): void => {
  if (typeof window === 'undefined') return

  const urlParams = new URLSearchParams(window.location.search)
  console.log('=== Shopify App Bridge v4 Diagnostics ===', {
    appBridgeAvailable: typeof window.shopify !== 'undefined',
    isEmbedded: window !== window.top,
    shop: urlParams.get('shop'),
    hasIdToken: !!urlParams.get('id_token'),
    hasApiKeyMeta: !!document.querySelector('meta[name="shopify-api-key"]'),
  })
}

/**
 * Validate Shopify App Bridge setup
 * The meta tag and App Bridge script are in index.html.
 * This function validates the setup and logs errors if misconfigured.
 */
export const initializeShopifyAppBridge = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const urlParams = new URLSearchParams(window.location.search)
  const shop = urlParams.get('shop')
  const host = urlParams.get('host')

  if (!shop || !host) return // Not in Shopify context

  // Validate meta tag has a valid client ID
  const existingMeta = document.querySelector('meta[name="shopify-api-key"]')
  const metaContent = existingMeta?.getAttribute('content')

  if (!metaContent || metaContent.length < 10 || metaContent.startsWith('%')) {
    console.error('[Shopify] App Bridge client ID not configured. Check VITE_SHOPIFY_CLIENT_ID build arg.')
  }
}

/**
 * Check if we're in Shopify context based on URL parameters
 * This can be used before App Bridge is fully loaded
 */
export const hasShopifyContext = (): boolean => {
  if (typeof window === 'undefined') return false

  const urlParams = new URLSearchParams(window.location.search)
  return !!(urlParams.get('shop') && (urlParams.get('host') || urlParams.get('hmac')))
}
